import axios from 'axios';
import log from 'lib/logger';
import showToast from 'lib/toast_notification';
import { reviewCheckAllPayload } from 'helpers/review_check_all';
import ApplicationController from './application_controller';

/**
 * @description "Reviewed" checkbox column on the billing claim indexes (ERA claim
 * details, EDI 837Ps, EDI claim statuses) and on each record's show page.
 *
 * Each row toggle PATCHes only its own row, and the header "check all" box is an
 * explicitly bulk action scoped to the CURRENT PAGE, so its toasts say "page".
 * Keep those lists paginated so the payload stays bounded.
 *
 * The optimistic flip is never trusted on its own: the endpoint answers with the
 * ids it wrote and the ids it skipped (out of scope, or deleted since the page
 * rendered), and the boxes are reconciled against that.
 *
 * Targets:
 *   checkbox — a row's checkbox (carries data-review-id)
 *   checkAll — the header box (index only)
 * Values:
 *   url              — the batch_update endpoint
 *   paramKey         — payload key, e.g. "era_claim_details"
 *   reloadOnSuccess  — true when a Reviewed filter is active, so the rows just
 *                      marked leave the result set and the pagination on screen
 *                      would otherwise skip a page of work
 */
export default class extends ApplicationController {
  static targets = ['checkbox', 'checkAll'];

  static values = {
    url: String,
    paramKey: String,
    reloadOnSuccess: { type: Boolean, default: false },
  };

  connect() {
    // One in-flight request per row id, so a rapid double toggle cannot land its
    // two responses out of order and leave the box opposite the stored value.
    this.pendingRequests = new Map();
    this.syncCheckAll();
  }

  disconnect() {
    this.pendingRequests.forEach((controller) => controller.abort());
    this.pendingRequests.clear();
  }

  async toggle(event) {
    const checkbox = event.target;
    const isChecked = checkbox.checked;
    const id = checkbox.dataset.reviewId;

    this.pendingRequests.get(id)?.abort();
    const abortController = new AbortController();
    this.pendingRequests.set(id, abortController);
    checkbox.disabled = true;

    try {
      const data = await this.submit({ [id]: String(isChecked) }, abortController.signal);

      if (this.skippedIds(data).includes(String(id))) {
        checkbox.checked = !isChecked;
        showToast('That row could not be updated.', { type: 'error' });
      } else {
        showToast(isChecked ? 'Marked as reviewed' : 'Marked as needs review');
      }
    } catch (error) {
      if (axios.isCancel(error)) return;

      checkbox.checked = !isChecked;
      showToast('The review status could not be saved. Please try again.', { type: 'error' });
      log.error(error);
    } finally {
      // Only the request that is still the row's current one may re-enable the
      // box; an aborted one must not unlock it under its replacement.
      if (this.pendingRequests.get(id) === abortController) {
        this.pendingRequests.delete(id);
        checkbox.disabled = false;
      }
      this.syncCheckAll();
    }
  }

  async toggleAll(event) {
    const checkAll = event.target;
    const isChecked = checkAll.checked;
    const boxes = this.checkboxTargets;

    if (boxes.length === 0) {
      // Nothing to act on: don't latch the box or send a PATCH the server rejects.
      checkAll.checked = false;
      return;
    }

    const previouslyChecked = boxes.filter((box) => box.checked);
    const rows = boxes.map((box) => ({ id: box.dataset.reviewId, checked: box.checked }));
    const changes = reviewCheckAllPayload(rows, isChecked);

    // Setting .checked programmatically does not fire `change`, so the per-row
    // handler stays quiet during a bulk update.
    boxes.forEach((box) => {
      box.checked = isChecked;
    });
    this.syncCheckAll();

    if (Object.keys(changes).length === 0) return;

    try {
      const data = await this.submit(changes);
      const skipped = this.skippedIds(data);

      if (skipped.length > 0) {
        boxes.forEach((box) => {
          if (skipped.includes(String(box.dataset.reviewId))) box.checked = !isChecked;
        });
        this.syncCheckAll();
        showToast(`${skipped.length} of these could not be updated.`, { type: 'error' });
        return;
      }

      if (this.reloadOnSuccessValue) {
        window.location.reload();
        return;
      }

      showToast(isChecked ? 'Marked page as reviewed' : 'Marked page as needs review');
    } catch (error) {
      boxes.forEach((box) => {
        box.checked = false;
      });
      previouslyChecked.forEach((box) => {
        box.checked = true;
      });
      this.syncCheckAll();
      showToast('The review status could not be saved. Please try again.', { type: 'error' });
      log.error(error);
    }
  }

  // Private

  async submit(changes, signal) {
    const { data } = await axios.patch(this.urlValue, { [this.paramKeyValue]: changes }, { signal });
    return data;
  }

  skippedIds(data) {
    return (data?.skipped || []).map(String);
  }

  syncCheckAll() {
    if (!this.hasCheckAllTarget) return;

    const boxes = this.checkboxTargets;
    if (boxes.length === 0) return;

    const checked = boxes.filter((box) => box.checked);
    // Partly-reviewed pages show the indeterminate dash rather than an empty box,
    // matching checkbox_select_all_controller. Clicking through from there marks
    // the page, which is the useful direction.
    this.checkAllTarget.checked = checked.length === boxes.length;
    this.checkAllTarget.indeterminate = checked.length > 0 && checked.length < boxes.length;
  }
}
